// B6 (docs/build-briefs/B6-barcode-receive.md). Reading a barcode out of a camera frame. The browser's own BarcodeDetector is used
// when it exists and supports at least one of the formats below; otherwise @zxing/library (Apache-2.0, pinned, bundled by the
// existing esbuild step, nothing fetched at runtime, no eval) decodes a frame drawn to a canvas. Both return the raw text of the
// first code found, or null. No DOM is touched except through the arguments, so the zxing path is tested in node.

import {
  BarcodeFormat,
  BinaryBitmap,
  ChecksumException,
  DecodeHintType,
  FormatException,
  HybridBinarizer,
  MultiFormatOneDReader,
  NotFoundException,
  RGBLuminanceSource,
} from "@zxing/library";

/** The symbologies a bottle, a can and a case carry: retail UPC and EAN, Code 128, Code 39 and the ITF-14 case code. */
export const NATIVE_FORMATS = [
  "ean_13",
  "ean_8",
  "upc_a",
  "upc_e",
  "code_128",
  "code_39",
  "itf",
] as const;

const ZXING_FORMATS: BarcodeFormat[] = [
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.CODE_128,
  BarcodeFormat.CODE_39,
  BarcodeFormat.ITF,
];

/** The pixel buffer shape both a canvas ImageData and a test fixture have. */
export interface RgbaFrame {
  readonly data: ArrayLike<number>;
  readonly width: number;
  readonly height: number;
}

/** RGBA to one grey byte per pixel, the layout RGBLuminanceSource reads. */
export function rgbaToLuminance(frame: RgbaFrame): Uint8ClampedArray {
  const { data, width, height } = frame;
  if (data.length < width * height * 4) {
    throw new RangeError("frame is smaller than its stated size");
  }
  const grey = new Uint8ClampedArray(width * height);
  for (let i = 0, p = 0; i < grey.length; i += 1, p += 4) {
    grey[i] = ((data[p] ?? 0) * 299 + (data[p + 1] ?? 0) * 587 + (data[p + 2] ?? 0) * 114) / 1000;
  }
  return grey;
}

// MultiFormatOneDReader and not MultiFormatReader: the latter logs a console warning for every frame in which a reader finds
// nothing (zxing 0.23 does not treat NotFoundException as a reader exception there), which is nearly every frame.
let reader: MultiFormatOneDReader | undefined;
let hints: Map<DecodeHintType, unknown> | undefined;

function zxingReader(): { reader: MultiFormatOneDReader; hints: Map<DecodeHintType, unknown> } {
  if (reader === undefined || hints === undefined) {
    hints = new Map<DecodeHintType, unknown>();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, ZXING_FORMATS);
    hints.set(DecodeHintType.TRY_HARDER, true);
    reader = new MultiFormatOneDReader(hints);
  }
  return { reader, hints };
}

/** Decodes one frame with zxing. Null when no code is found (the normal answer for most frames), also when a code is found but
 * fails its checksum: a misread is never reported as a code. */
export function decodeFrame(frame: RgbaFrame): string | null {
  const source = new RGBLuminanceSource(rgbaToLuminance(frame), frame.width, frame.height);
  const bitmap = new BinaryBitmap(new HybridBinarizer(source));
  try {
    const { reader: oneD, hints: readerHints } = zxingReader();
    return oneD.decode(bitmap, readerHints).getText();
  } catch (error) {
    if (
      error instanceof NotFoundException ||
      error instanceof ChecksumException ||
      error instanceof FormatException
    ) {
      return null;
    }
    throw error;
  }
}

interface NativeDetectorLike {
  detect(source: CanvasImageSource): Promise<{ rawValue: string }[]>;
}
interface NativeDetectorCtor {
  new (options: { formats: string[] }): NativeDetectorLike;
  getSupportedFormats?: () => Promise<string[]>;
}

/** What `window` offers; a parameter so a test can hand in a fake. */
export interface DecoderEnvironment {
  BarcodeDetector?: NativeDetectorCtor;
}

export interface FrameDecoder {
  readonly kind: "native" | "zxing";
  /** The first code in the current video frame, or null. */
  decode(video: HTMLVideoElement): Promise<string | null>;
}

/** Native when the browser can read at least one of our formats, zxing otherwise. */
export async function createFrameDecoder(
  env: DecoderEnvironment,
  canvasFactory: () => { canvas: HTMLCanvasElement; context: CanvasRenderingContext2D },
): Promise<FrameDecoder> {
  const Native = env.BarcodeDetector;
  if (Native !== undefined) {
    try {
      const supported =
        Native.getSupportedFormats === undefined
          ? [...NATIVE_FORMATS]
          : await Native.getSupportedFormats();
      const formats = NATIVE_FORMATS.filter((format) => supported.includes(format));
      if (formats.length > 0) {
        const detector = new Native({ formats: [...formats] });
        return {
          kind: "native",
          async decode(video) {
            const found = await detector.detect(video);
            return found[0]?.rawValue ?? null;
          },
        };
      }
    } catch (error) {
      // A detector that throws on construction (a platform without the service) is not a reason to stop scanning: say so and
      // use the bundled decoder.
      console.warn("BarcodeDetector unavailable, using the bundled decoder", error);
    }
  }
  const { canvas, context } = canvasFactory();
  return {
    kind: "zxing",
    async decode(video) {
      const width = video.videoWidth;
      const height = video.videoHeight;
      if (width === 0 || height === 0) return null;
      // A frame wider than 960 px costs time and finds nothing more.
      const scale = Math.min(1, 960 / width);
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      return decodeFrame(context.getImageData(0, 0, canvas.width, canvas.height));
    },
  };
}
